import { FC, useState } from 'react';
import { Box } from '@mui/material';

import Table from 'shared/components/widgets/table';

import Dialog from 'shared/components/widgets/dialog';

import ExternalDeliveryFilesDetail from '@components/file-detail/external-delivery-files';
import OperationHistoryDetail from '@components/file-detail/operation-history';
import EmployeeManagementDetail from '@components/file-detail/employee-management';
import { Classification, CustomerDeliveryFileStatus, CustomerDeliveryFileUploadStatus, DepartmentCode } from 'types';
import CustomerDeliveryFilesDetail from '@components/file-detail/customer-delivery-files';
import { ROUTES } from '../../../constants/routes';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { isAdmin } from '@utils/getRole.ts';

type FileManagementListProps = {
  isLoading?: boolean
  type: any;
  listData: any;
  isDetailOpen?: boolean;
  handleListData?: () => void;
  getFilteredList?: (data: any) => void;
  handlePagination?: (data: any) => void;
  setReload?: (reload: boolean) => void;
};

type CustomerRowData = {
  id: number | string;
  expirationYmd: string;
  lfsUploadSt: '50' | '60' | '90';
  otmSeq: string;
  otmSt: 'allow' | 'deny';
  otmSubject: string;
  otmfOriginName: string;
  otmfSize: number;
  regDt: string;
};

type ExternalRowData = {
  id: number | string;
  fileCount: number;
  regDt: string;
  shmDeptCd: DepartmentCode;
  shmLfsKind: Classification;
  shmSeq: string;
  shmSubject: string;
  shmfOriginName: string | null;
  shmfSize: number | null;
};

type EmployeeManagementRowData = {
  joinTp: 'google' | 'markany' | 'kakao';
  regDt: string;
  userEmail: string;
  userName: string;
  userSeq: string;
  userSt: '50' | '80' | '90';
  userUid: string;
};

type CustomerDeliverDownloadFileRowData = {
  fileTotalCnt: number;
  fileTotalSize: number;
  histDt: string;
  histSeq: string;
  otmSubject: string;
  remoteIp: string;
};

type ExternalDeliveryDownloadFileRowData = {
  fileTotalCnt: number;
  fileTotalSize: number;
  histDt: string;
  histSeq: string;
  remoteIp: string;
  shmDeptCd: DepartmentCode;
  shmLfsKind: Classification;
  shmSubject: string;
};

type CustomerListDataType = {
  page: number;
  size: number;
  adminConfigEnv: {
    cssJsVersion: string;
  } | null;
  sortName:
  | ''
  | 'otmSubject'
  | 'lfsUploadSt'
  | 'otmfOriginName'
  | 'otmSt'
  | 'expirationYmd'
  | 'regDt'
  | null;
  sortType: 'asc' | 'dsc' | '' | null;
  schOtmSt: CustomerDeliveryFileUploadStatus | '';
  schGroup: string;
  schKeyword: string;
  otmList:
  | [
    {
      otmSeq: string;
      otmSubject: string;
      otmfOriginName: null | string;
      otmfSize: number;
      otmSt: CustomerDeliveryFileStatus;
      expirationYmd: string;
      regDt: string;
      lfsUploadSt: CustomerDeliveryFileUploadStatus;
      regName: null | string;
    },
  ]
  | [];
  totalCount: number;
};

type RowData =
  | CustomerRowData
  | ExternalRowData
  | EmployeeManagementRowData
  | CustomerDeliverDownloadFileRowData
  | ExternalDeliveryDownloadFileRowData
  | CustomerListDataType
  | null;

const FileManagementList: FC<FileManagementListProps> = ({
  type,
  listData,
  isDetailOpen,
  getFilteredList,
  handlePagination,
  setReload,
  isLoading
}) => {
  const [openFileDetailDialog, setOpenFileDetailDialog] = useState(false);
  const [openedRowData, setOpenedRowData] = useState<RowData>(null);
  const [sortType, setSortType] = useState('asc');

  const handleOpenFileDetailDialog = () => setOpenFileDetailDialog(true);
  const handleCloseFileDetailDialog = () => {
    toast.dismiss('toastMsg')
    setOpenFileDetailDialog(false)
  };

  const rows = listData.rows;

  const columns = listData.columns;

  const isCustomerRowData = (data: RowData): data is CustomerRowData => {
    return (data as CustomerRowData)?.otmSeq !== undefined;
  };

  const isExternalRowData = (data: RowData): data is ExternalRowData => {
    return (data as ExternalRowData)?.shmSeq !== undefined;
  };

  const isEmployeeManagementRowData = (
    data: RowData,
  ): data is EmployeeManagementRowData => {
    return (data as EmployeeManagementRowData)?.userSeq !== undefined;
  };

  const isCustomerDeliveryDownloadFiles = (
    data: RowData,
  ): data is CustomerDeliverDownloadFileRowData => {
    return (data as CustomerDeliverDownloadFileRowData)?.histSeq !== undefined;
  };

  const isExternalDeliveryDownloadFiles = (
    data: RowData,
  ): data is ExternalDeliveryDownloadFileRowData => {
    return (data as ExternalDeliveryDownloadFileRowData)?.histSeq !== undefined;
  };

  const replaceSortKey = (key: string) => {
    switch (key) {
      case 'shmLfsKind':
        return 'lfsKind';
      case 'shmDeptCd':
        return 'deptCd';
      case 'schLfsUploadSt':
        return 'schOtmSt';
      default:
        return key;
    }
  };

  // 고객 전달용 파일
  const [customerListData, setCustomerListData] = useState<CustomerListDataType>({
    page: 1,
    size: 10,
    sortName: '',
    sortType: '',
    schOtmSt: '',
    schGroup: '',
    schKeyword: '',
    adminConfigEnv: null,
    otmList: [],
    totalCount: 0,
  });

  const navigate = useNavigate();

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 'calc(100vw - 500px)',
        }}
      >
        <Table
          rows={rows}
          columns={columns}
          sortModel={[]}
          onColumnHeaderClick={(columnData) => {
            if (!columnData?.colDef?.sortable) return;
            setSortType((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            if (getFilteredList)
              getFilteredList([
                { key: 'sortName', value: replaceSortKey(columnData.field) },
                { key: 'sortType', value: sortType },
              ]);
          }}
          onRowClick={(tableData) => {
            if (isDetailOpen) {
              if (type === 'CUSTOMER_DELIVERY' && tableData.row.lfsUploadSt === '60') {
                navigate(`${ROUTES.CUSTOMER_DELIVERY_FILES.UPDATE_FILE.path}/${tableData.row?.otmSeq}`, {
                  state: { retryUpload: true, fileData: tableData.row },
                });
                return;  // 이후 로직 실행하지 않음
              }
              if (type === 'CUSTOMER_DELIVERY' && tableData.row.lfsUploadSt === '90') {
                return;
              }
              setOpenedRowData(tableData.row);
              handleOpenFileDetailDialog();
            }
          }}
          rowCount={listData.totalCount}
          handlePagination={(pageNumber) => {
            if (handlePagination)
              handlePagination([{ key: 'page', value: pageNumber }]);
          }}
          currentPage={listData?.page}
          isLoading={isLoading}
        />
      </Box>
      <Dialog
        open={openFileDetailDialog}
        closeAction={handleCloseFileDetailDialog}
        width={1180}
      >
        {type === 'CUSTOMER_DELIVERY' && isCustomerRowData(openedRowData) && !isAdmin && (
          <>
            {openedRowData?.lfsUploadSt === '50' && (
              <CustomerDeliveryFilesDetail
                fileId={openedRowData?.otmSeq}
                closeDetailAction={handleCloseFileDetailDialog}
                serviceRequiredData={customerListData}
                setServiceRequiredData={setCustomerListData}
                setReload={setReload}
              />
            )}
          </>
        )}
        {type === 'EXTERNAL_DELIVERY' && isExternalRowData(openedRowData) && (
          <ExternalDeliveryFilesDetail
            fileId={openedRowData?.shmSeq}
            listData={listData}
            closeDetailAction={handleCloseFileDetailDialog}
          />
        )}
        {type === 'EMPLOYEE_MANAGEMENT' &&
          isEmployeeManagementRowData(openedRowData) && (
            <EmployeeManagementDetail userSeq={openedRowData?.userSeq} />
          )}
        {typeof type === 'object' &&
          type.root === 'OPERATION_HISTORY' &&
          (type.children.CUSTOMER_DELIVERY_DOWNLOAD_FILES ===
            'CUSTOMER_DELIVERY_DOWNLOAD_FILES'
            ? isCustomerDeliveryDownloadFiles(openedRowData) && (
              <OperationHistoryDetail
                histSeq={openedRowData?.histSeq}
                type={type.children.CUSTOMER_DELIVERY_DOWNLOAD_FILES}
              />
            )
            : isExternalDeliveryDownloadFiles(openedRowData) && (
              <OperationHistoryDetail
                histSeq={openedRowData?.histSeq}
                type={type.children.EXTERNAL_DELIVERY_DOWNLOAD_FILES}
              />
            ))}
      </Dialog>
    </Box>
  );
};

export default FileManagementList;
